// src/Terms.js
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from './components/Layout';

const translations = {
  en: {
    title: "Terms and Conditions",
    effectiveDate: "Effective Date: 26/06/2025",
    lastUpdated: "Last Updated: 26/06/2025",
    acknowledgement: "By using our Service, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.",
    contactText: "For questions about these Terms or our Service, please use our",
    contactLink: "contact form",
    sections: [
      {
        heading: "1. Acceptance of Terms",
        content: "By accessing and using the Toyota ERC Headunit Unlock Tool (\"Service\") available at ercunlock.com (\"Website\"), you agree to be bound by these Terms and Conditions (\"Terms\"). If you do not agree to these Terms, please do not use our Service."
      },
      {
        heading: "2. Description of Service",
        content: "Our Service provides a tool to generate Emergency Rescue Codes (ERC) for unlocking certain Japanese Toyota vehicle head units. The Service includes:",
        list: [
          "Free ERC code generation (limited to 2 uses per browser session)",
          "Paid credit system for additional unlocks",
          "Multi-language support",
          "Educational information about ERC codes"
        ]
      },
      {
        heading: "3. Credit System and Browser Sessions",
        subSections: [
          {
            heading: "3.1 Credit Storage",
            list: [
              "Credits and free unlock counts are stored locally in your web browser session",
              "Credits are NOT transferable between different browsers, devices, or browser sessions",
              "Clearing your browser data, using incognito/private browsing, or switching browsers will result in loss of credits",
              "We do not maintain server-side credit accounts or user profiles"
            ]
          },
          {
            heading: "3.2 Free Unlocks",
            list: [
              "Each new browser session receives 2 free ERC unlocks",
              "Free unlocks cannot be restored once used",
              "Free unlocks are reset only when browser data is cleared or a new browser/device is used"
            ]
          },
          {
            heading: "3.3 Paid Credits",
            list: [
              "Paid credits are purchased through our secure payment processor (Stripe)",
              "Credits are immediately available after successful payment completion",
              "Credits are bound to the specific browser session where the purchase was made",
              "Important: Credits will be lost if you clear browser data, switch browsers, or use private/incognito mode"
            ]
          }
        ]
      },
      {
        heading: "4. Payment Terms",
        subSections: [
          {
            heading: "4.1 Payment Processing",
            list: [
              "All payments are processed securely through Stripe, Inc.",
              "We accept major credit cards and payment methods supported by Stripe",
              "Prices are displayed in USD and include all applicable taxes"
            ]
          },
          {
            heading: "4.2 Refund Policy",
            list: [
              "Due to the digital nature of our service and instant delivery of codes, all sales are final",
              "Refunds are not provided except as required by applicable law",
              "If you experience technical issues preventing code generation, please contact support"
            ]
          }
        ]
      },
      {
        heading: "5. Proper Use of Service",
        subSections: [
          {
            heading: "5.1 Authorized Use",
            list: [
              "This Service is intended for legitimate vehicle owners seeking to unlock their own Toyota head units",
              "You must own or have legal authorization to modify the vehicle in question",
              "The Service is for personal, non-commercial use only"
            ]
          },
          {
            heading: "5.2 Prohibited Activities",
            content: "You agree NOT to:",
            list: [
              "Use the Service for vehicles you do not own or lack authorization to modify",
              "Resell, redistribute, or commercialize generated ERC codes",
              "Attempt to reverse engineer, decompile, or create derivative works of our Service",
              "Use automated tools, bots, or scripts to access the Service",
              "Violate any applicable laws or regulations"
            ]
          }
        ]
      },
      {
        heading: "6. Disclaimer of Warranties",
        subSections: [
          {
            heading: "6.1 Service Availability",
            list: [
              "The Service is provided \"as is\" without warranties of any kind",
              "We do not guarantee uninterrupted access or error-free operation",
              "Generated codes are provided without warranty of compatibility or effectiveness"
            ]
          },
          {
            heading: "6.2 Vehicle Compatibility",
            list: [
              "While we support many Toyota head unit models, we cannot guarantee compatibility with all vehicles",
              "Users are responsible for verifying compatibility before purchasing credits",
              "We are not responsible for any damage to vehicles or head units"
            ]
          }
        ]
      },
      {
        heading: "7. Limitation of Liability",
        content: "To the maximum extent permitted by law:",
        list: [
          "Our total liability shall not exceed the amount paid for credits in the transaction giving rise to the claim",
          "We are not liable for indirect, incidental, special, consequential, or punitive damages",
          "We are not responsible for vehicle damage, data loss, or technical issues arising from Service use"
        ]
      },
      {
        heading: "8. Privacy and Data Protection",
        subSections: [
          {
            heading: "8.1 Data Collection",
            list: [
              "We collect minimal personal information necessary for payment processing",
              "ERC codes entered are processed locally and not stored on our servers",
              "Payment information is handled exclusively by Stripe according to their privacy policy"
            ]
          },
          {
            heading: "8.2 Browser Data",
            list: [
              "Credit information is stored locally in your browser using localStorage",
              "We do not have access to or control over locally stored credit data",
              "Users are responsible for protecting their browser data and credit information"
            ]
          }
        ]
      },
      {
        heading: "9. Intellectual Property",
        subSections: [
          {
            heading: "9.1 Service Ownership",
            list: [
              "The Service, including all software, design, and content, is owned by us or our licensors",
              "You receive a limited, non-exclusive license to use the Service for personal purposes",
              "All trademarks, logos, and brand names are property of their respective owners"
            ]
          },
          {
            heading: "9.2 User-Generated Content",
            list: [
              "You retain ownership of any ERC codes or data you input",
              "You grant us a limited license to process your inputs for Service functionality"
            ]
          }
        ]
      },
      {
        heading: "10. Third-Party Services",
        subSections: [
          {
            heading: "10.1 Payment Processing",
            list: [
              "Payment processing is handled by Stripe, Inc., subject to their terms of service",
              "We are not responsible for Stripe's services, fees, or policies"
            ]
          },
          {
            heading: "10.2 External Links",
            list: [
              "Our Service may contain links to third-party websites",
              "We are not responsible for the content or practices of linked websites"
            ]
          }
        ]
      },
      {
        heading: "11. Modifications to Terms",
        content: "We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting on our Website. Continued use of the Service constitutes acceptance of modified Terms."
      },
      {
        heading: "12. Geographic Restrictions",
        content: "Our Service is available worldwide, but users are responsible for compliance with local laws and regulations regarding vehicle modification."
      },
      {
        heading: "13. Account Termination",
        content: "We reserve the right to:",
        list: [
          "Suspend or terminate access to the Service for Terms violations",
          "Refuse service to any user at our discretion",
          "Remove or disable any content that violates these Terms"
        ]
      },
      {
        heading: "14. Governing Law and Dispute Resolution",
        subSections: [
          {
            heading: "14.1 Governing Law",
            content: "These Terms are governed by the laws of New Zealand, without regard to conflict of law principles."
          },
          {
            heading: "14.2 Dispute Resolution",
            content: "Any disputes arising from these Terms or Service use shall be resolved through the appropriate courts in New Zealand."
          }
        ]
      },
      {
        heading: "15. Severability",
        content: "If any provision of these Terms is found to be unenforceable, the remaining provisions shall remain in full force and effect."
      },
      {
        heading: "16. Entire Agreement",
        content: "These Terms constitute the entire agreement between you and us regarding the Service and supersede all prior agreements and understandings."
      }
    ]
  },
  ja: {
    title: "利用規約",
    effectiveDate: "発効日：2025年6月26日",
    lastUpdated: "最終更新：2025年6月26日",
    acknowledgement: "本サービスをご利用いただくことで、お客様はこの利用規約を読み、理解し、同意したものとみなします。",
    contactText: "本規約またはサービスに関するご質問は、",
    contactLink: "お問い合わせフォーム",
    sections: [
      { heading: "1. 規約への同意", content: "ercunlock.comで提供されるToyota ERCヘッドユニットロック解除ツール（「サービス」）にアクセスまたは使用することで、これらの利用規約（「規約」）に拘束されることに同意したものとみなします。" },
      { heading: "2. サービスの説明", content: "本サービスは、特定のToyota車両ヘッドユニットのロックを解除するためのERC（緊急解除コード）を生成するツールを提供します。", list: ["無料ERC生成（ブラウザセッションごとに2回まで）", "追加ロック解除用の有料クレジットシステム", "多言語サポート", "ERCコードに関する情報提供"] },
      { heading: "3. クレジットシステムとブラウザセッション", subSections: [
        { heading: "3.1 クレジットの保存", list: ["クレジットはブラウザのローカルセッションに保存されます", "クレジットは異なるブラウザやデバイス間で移行できません", "ブラウザデータの削除やシークレットモードの使用によりクレジットが失われます", "サーバー側のアカウントは管理していません"] },
        { heading: "3.2 無料ロック解除", list: ["新しいブラウザセッションごとに2回の無料ロック解除が提供されます", "使用済みの無料ロック解除は復元できません"] },
        { heading: "3.3 有料クレジット", list: ["有料クレジットはStripeを通じて購入します", "支払い完了後すぐに利用可能になります", "クレジットは購入したブラウザセッションに紐付けられます"] }
      ]},
      { heading: "4. 支払い条件", subSections: [
        { heading: "4.1 支払い処理", list: ["すべての支払いはStripe, Inc.を通じて安全に処理されます", "主要なクレジットカードおよびStripe対応の支払い方法が利用可能です"] },
        { heading: "4.2 返金ポリシー", list: ["デジタルサービスの性質上、すべての販売は最終とします", "適用法が要求する場合を除き、返金は提供されません"] }
      ]},
      { heading: "5. サービスの適切な利用", subSections: [
        { heading: "5.1 許可された利用", list: ["本サービスは自身のToyotaヘッドユニットをロック解除しようとする正当な車両所有者を対象としています", "対象車両の所有権または合法的な改修権限が必要です"] },
        { heading: "5.2 禁止事項", content: "以下の行為を禁止します：", list: ["所有していない車両への使用", "生成されたERCコードの転売や商業利用", "サービスのリバースエンジニアリング", "ボットや自動化ツールの使用", "適用法の違反"] }
      ]},
      { heading: "6. 保証の免責", content: "本サービスは「現状のまま」提供され、いかなる種類の保証も行いません。" },
      { heading: "7. 責任の制限", content: "適用法の最大限の範囲で、当社の責任は当該請求の原因となった取引で支払われた金額を超えないものとします。" },
      { heading: "8. プライバシーとデータ保護", content: "入力されたERCコードはローカルで処理され、サーバーには保存されません。支払い情報はStripeが管理します。" },
      { heading: "9. 知的財産", content: "本サービスのすべてのソフトウェア、デザイン、コンテンツは当社またはそのライセンサーが所有しています。" },
      { heading: "10. 準拠法", content: "本規約はニュージーランド法に準拠します。" }
    ]
  },
  mn: {
    title: "Үйлчилгээний нөхцөл",
    effectiveDate: "Хүчин төгөлдөр болсон огноо: 2025/06/26",
    lastUpdated: "Сүүлд шинэчлэгдсэн: 2025/06/26",
    acknowledgement: "Манай үйлчилгээг ашигласнаар та эдгээр нөхцөлийг уншиж, ойлгож, зөвшөөрсөн гэж тооцогдоно.",
    contactText: "Эдгээр нөхцөл эсвэл үйлчилгээний талаар асуулт байвал манай",
    contactLink: "холбоо барих хэлбэр",
    sections: [
      { heading: "1. Нөхцөл зөвшөөрөх", content: "ercunlock.com-д байрлах Toyota ERC Headunit Unlock Tool-д хандаж ашигласнаар та эдгээр Үйлчилгээний нөхцөлийг хүлээн зөвшөөрч байна." },
      { heading: "2. Үйлчилгээний тайлбар", content: "Бид тодорхой Toyota тээврийн хэрэгслийн толгой төхөөрөмжийг нээхийн тулд ERC код үүсгэх хэрэгсэл санал болгодог.", list: ["Үнэгүй ERC код үүсгэх (сессид 2 удаа)", "Нэмэлт нээлтийн төлбөртэй кредит систем", "Олон хэлний дэмжлэг"] },
      { heading: "3. Кредит систем", content: "Кредитүүд таны браузерт локалд хадгалагдана. Өөр браузер эсвэл төхөөрөмж ашиглах үед кредит шилжихгүй. Stripe-ээр дамжуулан кредит худалдаж авах боломжтой." },
      { heading: "4. Буцаан олголтын бодлого", content: "Тоон үйлчилгээний шинж чанараас шалтгаалан бүх борлуулалт эцсийнх. Хуулийн шаардлагаас бусад тохиолдолд буцаан олгохгүй." },
      { heading: "5. Зохистой ашиглалт", content: "Энэ үйлчилгээ зөвхөн өөрийн Toyota толгой төхөөрөмжийг нээхийг хүссэн хуулийн эзэмшигчдэд зориулагдсан." },
      { heading: "6. Хариуцлагын хязгаарлалт", content: "Манай нийт хариуцлага тухайн гүйлгээнд төлсөн дүнгээс хэтрэхгүй." },
      { heading: "7. Хамаарах хууль", content: "Эдгээр нөхцөл Шинэ Зеландын хуулиар зохицуулагдана." }
    ]
  },
  ur: {
    title: "شرائط و ضوابط",
    effectiveDate: "مؤثر تاریخ: 26/06/2025",
    lastUpdated: "آخری تازہ کاری: 26/06/2025",
    acknowledgement: "ہماری سروس استعمال کر کے آپ تصدیق کرتے ہیں کہ آپ نے یہ شرائط پڑھی، سمجھی اور ان سے متفق ہیں۔",
    contactText: "ان شرائط یا ہماری سروس کے بارے میں سوالات کے لیے ہمارا",
    contactLink: "رابطہ فارم",
    sections: [
      { heading: "1. شرائط کی قبولیت", content: "ercunlock.com پر دستیاب Toyota ERC Headunit Unlock Tool تک رسائی حاصل کر کے، آپ ان شرائط و ضوابط سے متفق ہوتے ہیں۔" },
      { heading: "2. سروس کی تفصیل", content: "ہماری سروس مخصوص Toyota گاڑیوں کے ہیڈ یونٹ کو ان لاک کرنے کے لیے ERC کوڈ تیار کرنے کا ٹول فراہم کرتی ہے۔", list: ["مفت ERC کوڈ جنریشن (فی سیشن 2 بار)", "اضافی ان لاکس کے لیے ادائیگی والا کریڈٹ سسٹم", "کثیر لسانی معاونت"] },
      { heading: "3. کریڈٹ سسٹم", content: "کریڈٹس آپ کے براؤزر میں مقامی طور پر محفوظ ہوتے ہیں۔ یہ مختلف براؤزرز یا ڈیوائسز کے درمیان منتقل نہیں ہو سکتے۔ کریڈٹس Stripe کے ذریعے خریدے جاتے ہیں۔" },
      { heading: "4. واپسی کی پالیسی", content: "ڈیجیٹل سروس کی نوعیت کی وجہ سے تمام فروخت حتمی ہیں۔ قابل اطلاق قانون کی ضرورت کے علاوہ رقم واپس نہیں کی جاتی۔" },
      { heading: "5. سروس کا مناسب استعمال", content: "یہ سروس صرف اپنے Toyota ہیڈ یونٹ کو ان لاک کرنے کے خواہشمند جائز مالکان کے لیے ہے۔" },
      { heading: "6. ذمہ داری کی حد", content: "ہماری کل ذمہ داری اس لین دین میں ادا کی گئی رقم سے زیادہ نہیں ہوگی۔" },
      { heading: "7. حکمران قانون", content: "یہ شرائط نیوزی لینڈ کے قوانین کے تحت چلتی ہیں۔" }
    ]
  },
  ru: {
    title: "Условия использования",
    effectiveDate: "Дата вступления в силу: 26.06.2025",
    lastUpdated: "Последнее обновление: 26.06.2025",
    acknowledgement: "Используя наш Сервис, вы подтверждаете, что прочитали, поняли и согласились с настоящими Условиями.",
    contactText: "По вопросам об этих Условиях или нашем Сервисе используйте нашу",
    contactLink: "контактную форму",
    sections: [
      { heading: "1. Принятие условий", content: "Получая доступ к инструменту Toyota ERC Headunit Unlock Tool на ercunlock.com и используя его, вы соглашаетесь с настоящими Условиями использования." },
      { heading: "2. Описание сервиса", content: "Наш Сервис предоставляет инструмент для генерации кодов ERC для разблокировки головных устройств Toyota.", list: ["Бесплатная генерация ERC (2 раза за сессию)", "Платная система кредитов для дополнительных разблокировок", "Многоязычная поддержка"] },
      { heading: "3. Система кредитов", content: "Кредиты хранятся локально в вашем браузере и не переносятся между браузерами или устройствами. Кредиты приобретаются через Stripe." },
      { heading: "4. Политика возврата", content: "В связи с цифровым характером сервиса все продажи являются окончательными. Возврат средств не предусмотрен, за исключением случаев, предусмотренных законом." },
      { heading: "5. Допустимое использование", content: "Сервис предназначен для законных владельцев транспортных средств, желающих разблокировать собственное головное устройство Toyota." },
      { heading: "6. Ограничение ответственности", content: "Наша совокупная ответственность не превышает сумму, уплаченную в соответствующей транзакции." },
      { heading: "7. Применимое право", content: "Настоящие Условия регулируются законодательством Новой Зеландии." }
    ]
  }
};

const Terms = ({ language = 'en' }) => {
  const t = translations[language] || translations.en;
  const prefix = language === 'en' ? '' : `/${language}`;

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ur' ? 'rtl' : 'ltr';
    document.title = `${t.title} - ERC Unlock`;
  }, [language, t.title]);

  const renderSection = (section, i) => (
    <div key={i}>
      <h2>{section.heading}</h2>
      {section.content && <p>{section.content}</p>}
      {section.list && <ul>{section.list.map((item, j) => <li key={j}>{item}</li>)}</ul>}
      {section.subSections && section.subSections.map((sub, j) => (
        <div key={j}>
          <h3>{sub.heading}</h3>
          {sub.content && <p>{sub.content}</p>}
          {sub.list && <ul>{sub.list.map((item, k) => <li key={k}>{item}</li>)}</ul>}
        </div>
      ))}
    </div>
  );

  return (
    <Layout language={language}>
      <div className="content-card">
        <h1>{t.title}</h1>
        <p className="subtitle"><strong>{t.effectiveDate}</strong></p>

        <div className="prose">
          {t.sections.map((section, i) => renderSection(section, i))}

          <h2>17. {language === 'ja' ? 'お問い合わせ' : language === 'mn' ? 'Холбоо барих' : language === 'ur' ? 'رابطہ معلومات' : language === 'ru' ? 'Контактная информация' : 'Contact Information'}</h2>
          <p>
            {t.contactText}{' '}
            <Link to={`${prefix}/contact`} style={{ color: '#2563eb', textDecoration: 'underline' }}>
              {t.contactLink}
            </Link>
            {language === 'ur' ? ' استعمال کریں۔' : language === 'ja' ? 'をご利用ください。' : language === 'mn' ? '-г ашиглана уу.' : language === 'ru' ? '.' : '.'}
          </p>

          <hr />
          <p><strong>{t.lastUpdated}</strong></p>
          <p>{t.acknowledgement}</p>
        </div>
      </div>
    </Layout>
  );
};

export default Terms;
